import "./style.scss";
import RenamePopup, {
  InputEditDataType
} from "popups/rename-model/rename-popup";
import { useContext, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { message, Tooltip } from "antd";
import { ErrorMessages } from "constants/index";
import FeatureEntityTaskDetailCard from "components/task-detail-card/feature-entity-task-detail-card";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import TimelineDurations from "./components/one-timeline-durations";
import {
  TDateBlockPermissions,
  TEntityDateblock,
  TGoverningSource,
  TWidthCalculation
} from "./models";
import TimelineIconsOnly from "./components/two-timeline-icons";
import TimelineMilestones from "./components/three-timeline-milestones";
import { APPLY_WORKFLOW_MUTATION } from "./queries/mutation";

const keyLoadingMessage = "loadingMessagekey";

function EntityDateblock(props: {
  entity: TEntityDateblock | undefined;
  governingSource?: TGoverningSource;
  afterChange?: () => void;
  permissions: TDateBlockPermissions;
  setApiCallLoading?: (isLoading: boolean) => void;
  entityHeaders?: any;
}) {
  const {
    entity,
    governingSource,
    afterChange,
    permissions,
    setApiCallLoading,
    entityHeaders
  } = props;
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameDataInput, setRenameDataInput] = useState<InputEditDataType>({
    value: "",
    id: ""
  });

  const { gqlClientForProject } = useContext(ProjectContext);

  const [applyWorkflowMutation] = useCIQMutation(APPLY_WORKFLOW_MUTATION, {
    client: gqlClientForProject
  });

  if (!entity) return null;
  const {
    workflow_instance_milestones: workflowInstanceMilestones,
    feature_workflow_instance: featureWorkflowInstance,
    feature
  } = entity;
  const { show_float: showFloat = true } = feature;

  if (!workflowInstanceMilestones || !featureWorkflowInstance) return null;

  // Sort by sequence_no
  const milestones = workflowInstanceMilestones.sort(
    (a, b) => a.sequence_no - b.sequence_no
  );

  const { length: offsetCount } = milestones.filter(
    (m) => m.feature_workflow_instance_offsets_as_src.length
  );

  const isLastOffset = offsetCount === milestones.length;

  const blockMargin = 24;
  const milestonesWidth = 144;
  const leftpaddingIconTimeline = 65;
  const timelineWidth = milestonesWidth + blockMargin;
  const lastLineWidth = 160;
  const lastOffsetWidth = isLastOffset ? 0 : -95;
  const totalDateBlockWidth =
    (milestones.length + 1.7) * timelineWidth + lastLineWidth + lastOffsetWidth;

  const widthCal = {
    blockMargin,
    milestonesWidth,
    leftpaddingIconTimeline,
    timelineWidth,
    lastLineWidth
  } as TWidthCalculation;

  const calculateCurrentMilestone = () => {
    for (let index = milestones.length - 1; index >= 0; index = -(1 - index)) {
      const mile = milestones[index];
      if (mile.actual_date) {
        if (index === milestones.length - 1) return undefined;
        return milestones[index + 1];
      }
    }

    return milestones.length ? milestones[0] : undefined;
  };

  const currentMilestone = calculateCurrentMilestone();

  const governingTaskDiv =
    governingSource?.type === "Linked Activity" ? (
      <Tooltip
        className="govering-db-toolitip"
        overlayStyle={{ maxWidth: "300px" }}
        title={
          <FeatureEntityTaskDetailCard governingSource={governingSource} />
        }
        placement="top"
        data-testid="ed-governing-tooltip"
      >
        <div
          className="h-6 w-full border border-solid  border-gray-400 flex items-center px-1 cursor-pointer text-xs"
          data-testid="ed-governing-task-div"
        >
          <b className="mt-1 mr-1" data-testid="ed-governing-indicator">
            *
          </b>{" "}
          <div className="grow truncate" data-testid="ed-governing-name">
            {governingSource.name || ""}
          </div>
        </div>
      </Tooltip>
    ) : null;

  const applyWorkflowChange = async (data: any) => {
    setApiCallLoading?.(true);
    message.loading({
      content: "Recalculating...",
      key: keyLoadingMessage,
      duration: 60000
    });
    const input = [
      {
        feature_instance_id: featureWorkflowInstance.feature_instance_id,
        workflow_instance_id: featureWorkflowInstance.id,
        ...data
      }
    ];

    const res = await applyWorkflowMutation({
      variables: {
        input,
        metadata: {
          request_source: "DETAIL_PAGE"
        }
      }
    });
    if (res.data) {
      message.success({
        content: "Updated successfully!",
        key: keyLoadingMessage,
        duration: 3
      });

      if (afterChange) {
        afterChange();
      }
    } else {
      message.error({
        content: ErrorMessages.SomethingWentWrong,
        key: keyLoadingMessage,
        duration: 3
      });
    }
    setApiCallLoading?.(false);
  };

  const editOffsetDateBlock = async (newOffset: string, id: string) => {
    const set = {} as any;
    if (renameDataInput.identityName === "WorkflowInstanceOffset") {
      set.duration = newOffset;

      applyWorkflowChange({
        offsets: [
          {
            id,
            duration: Number(newOffset)
          }
        ]
      });
    }

    if (renameDataInput.identityName === "WorkflowInstanceFloat") {
      applyWorkflowChange({
        pre_WF_start_float: Number(newOffset)
      });
    }
  };

  return (
    <div
      style={{ width: totalDateBlockWidth - 110 }}
      className="flex flex-col shrink-0 py-8 justify-center entity-date-block px-5"
      data-testid="ed-root"
    >
      <TimelineDurations
        milestones={milestones}
        widthCal={widthCal}
        featureWorkflowInstance={featureWorkflowInstance}
        setIsRenameModalOpen={setIsRenameModalOpen}
        setRenameDataInput={setRenameDataInput}
        showFloat={showFloat}
        permissions={permissions}
        data-testid="ed-timeline-durations"
      />
      <TimelineIconsOnly
        applyWorkflowChange={applyWorkflowChange}
        milestones={milestones}
        currentMilestone={currentMilestone}
        widthCal={widthCal}
        featureWorkflowInstance={featureWorkflowInstance}
        showFloat={showFloat}
        setIsRenameModalOpen={setIsRenameModalOpen}
        setRenameDataInput={setRenameDataInput}
        governingSource={governingSource}
        permissions={permissions}
        entityHeaders={entityHeaders}
        data-testid="ed-timeline-icons"
      />
      <TimelineMilestones
        applyWorkflowChange={applyWorkflowChange}
        milestones={milestones}
        currentMilestone={currentMilestone}
        widthCal={widthCal}
        featureWorkflowInstance={featureWorkflowInstance}
        governingTaskDiv={governingTaskDiv}
        permissions={permissions}
        data-testid="ed-timeline-milestones"
      />

      {isRenameModalOpen && (
        <RenamePopup
          isModalOpen={isRenameModalOpen}
          setIsModalOpen={setIsRenameModalOpen}
          inputData={renameDataInput}
          onOk={editOffsetDateBlock}
          title="Edit duration"
          okName="Save"
          data-testid="ed-rename-popup"
        />
      )}
    </div>
  );
}
export default EntityDateblock;
